import api from "@utils/api.client"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

export interface Property {
  id: string
  address: string
  description?: string
  status: string
  postalCode: string
  city: string
}

interface PropertyStore {
  properties: Property[]
  loading: boolean
  error: string | null
  fetchAll: () => Promise<void>
  addProperty: (property: Omit<Property, "id">) => Promise<void>
  updateProperty: (id: string, property: Partial<Omit<Property, "id">>) => Promise<void>
  removeProperty: (id: string) => Promise<void>
}

export const usePropertyStore = create<PropertyStore>()(
  devtools((set, get) => ({
    properties: [],
    loading: false,
    error: null,
    fetchAll: async () => {
      set({ loading: true, error: null })

      if (get().properties.length > 0) {
        return get().properties
      }
      try {
        const response = await api.get<Property[]>("properties-to-rent")
        set({ properties: response.data, loading: false })
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },
    addProperty: async (property) => {
      set({ loading: true, error: null })
      try {
        const newProperty = await api.post<Property>("properties-to-rent", property)
        set((state) => ({
          properties: [...state.properties, newProperty.data],
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },
    updateProperty: async (id, property) => {
      set({ loading: true, error: null })
      try {
        const existing = get().properties.find((prop) => prop.id === id)
        if (!existing) {
          throw new Error("Property not found")
        }
        const merged = { ...existing, ...property }
        Reflect.deleteProperty(merged, "id")
        const { data: updatedProperty } = await api.post<Property>(`properties-to-rent/${id}`, merged)

        set((state: PropertyStore) => ({
          properties: state.properties.map((prop) => (prop.id === id ? updatedProperty : prop)),
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },

    removeProperty: async (id) => {
      set({ loading: true, error: null })
      try {
        await api.post<Property>(`properties-to-rent/${id}/delete`)
        set((state) => ({
          properties: state.properties.filter((prop) => prop.id !== id),
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },
  })),
)
